// Top discos por artista (Tarea 19): mostrar y agregar DISCOS agrupados por artista.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAsync } from '../ui/useAsync.js';
import { AsyncBoundary, Card, Icon, PageHeader } from '../ui/components.js';
import type { TopRecord } from '../types.js';

/**
 * Clave de deduplicación de un disco: artista + álbum normalizados (minúsculas y
 * sin espacios extremos), mismo criterio que el backend (`lower(btrim(...))`).
 */
function dedupeKey(item: TopRecord): string {
  const artist = item.artist_name.trim().toLowerCase();
  const album = (item.album ?? '').trim().toLowerCase();
  return `${artist}::${album}`;
}

/**
 * Descarta discos repetidos (mismo artista + álbum), conservando la primera
 * aparición. La curaduría automática ya deduplica al insertar, pero esto evita
 * mostrar duplicados provenientes de altas manuales o datos históricos.
 */
function dedupeAlbums(items: TopRecord[]): TopRecord[] {
  const seen = new Set<string>();
  const unique: TopRecord[] = [];
  for (const item of items) {
    const key = dedupeKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
  }
  return unique;
}

/** Agrupa los discos del top por artista, preservando el orden de aparición. */
function groupByArtist(items: TopRecord[]): { artist: string; albums: TopRecord[] }[] {
  const order: string[] = [];
  const byArtist = new Map<string, TopRecord[]>();
  for (const item of items) {
    const key = item.artist_name;
    if (!byArtist.has(key)) {
      byArtist.set(key, []);
      order.push(key);
    }
    byArtist.get(key)!.push(item);
  }
  return order.map((artist) => ({ artist, albums: byArtist.get(artist)! }));
}

export function TopView() {
  const { t } = useTranslation();
  const api = useApi();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('top', 'read_write');
  const list = useAsync(() => api.top.list(), []);
  const [adding, setAdding] = useState(false);
  const [artistFilter, setArtistFilter] = useState('');

  async function remove(item: TopRecord) {
    if (!confirm(t('top.removeConfirm'))) return;
    await api.top.remove(item.id);
    list.reload();
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('top.title')}
        subtitle={t('top.subtitle')}
        back
        meta={
          canEdit ? (
            <button className="btn-secondary btn-sm" type="button" onClick={() => setAdding((a) => !a)}>
              <Icon name={adding ? 'close' : 'add'} className="text-[16px]" /> {adding ? t('top.close') : t('top.addRecord')}
            </button>
          ) : undefined
        }
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        {t('top.intro')}
      </p>
      {adding && canEdit && <AddForm onCreated={() => { setAdding(false); list.reload(); }} />}
      <div className="field-slot flex items-center gap-space-xs">
        <Icon name="search" className="text-[18px] text-on-surface-variant shrink-0" />
        <input
          className="field-input !border-0 !bg-transparent !p-0 w-full"
          type="search"
          placeholder={t('top.searchPlaceholder')}
          value={artistFilter}
          onChange={(e) => setArtistFilter(e.target.value)}
        />
        {artistFilter !== '' && (
          <button className="btn-ghost btn-sm shrink-0" type="button" onClick={() => setArtistFilter('')} aria-label={t('top.clearSearch')}>
            <Icon name="close" className="text-[16px]" />
          </button>
        )}
      </div>
      <AsyncBoundary state={list} empty={(d) => d.items.length === 0}>
        {(d) => <ArtistGroups items={d.items} filter={artistFilter} canEdit={canEdit} onRemove={remove} />}
      </AsyncBoundary>
      </div>
    </div>
  );
}

/** ¿El registro coincide con el texto buscado (artista, disco o año)? */
function matchesFilter(item: TopRecord, needle: string): boolean {
  if (needle === '') return true;
  const haystacks = [
    item.artist_name,
    item.album ?? '',
    item.year != null ? String(item.year) : '',
  ];
  return haystacks.some((value) => value.toLowerCase().includes(needle));
}

/** Lista de discos agrupados por artista, con filtro por artista, disco o año. */
function ArtistGroups({
  items,
  filter,
  canEdit,
  onRemove,
}: {
  items: TopRecord[];
  filter: string;
  canEdit: boolean;
  onRemove: (item: TopRecord) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const groups = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const unique = dedupeAlbums(items);
    const source = needle === ''
      ? unique
      : unique.filter((item) => matchesFilter(item, needle));
    return groupByArtist(source);
  }, [items, filter]);

  if (groups.length === 0) {
    return (
      <p className="font-body-sm text-body-sm text-on-surface-variant px-space-xs">
        {t('top.noMatches', { filter: filter.trim() })}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-space-md">
      {groups.map((group) => (
        <Card key={group.artist} title={group.artist} icon="star">
          <ul className="flex flex-col gap-space-sm">
            {group.albums.map((item) => (
              <li key={item.id} className="surface-rack-card !p-space-sm flex items-center justify-between gap-space-sm">
                <div className="min-w-0">
                  <div className="font-body-md text-body-md text-on-surface truncate">
                    {item.album ?? t('top.untitledRecord')}
                  </div>
                  {item.year != null && (
                    <div className="font-body-sm text-body-sm text-on-surface-variant truncate">
                      {item.year}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <button className="btn-ghost btn-sm !text-priority-urgent shrink-0" type="button" onClick={() => void onRemove(item)}>
                    <Icon name="delete" className="text-[16px]" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}

function AddForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [artist, setArtist] = useState('');
  const [album, setAlbum] = useState('');
  const [year, setYear] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.top.create({ artist_name: artist, album: album || null, song: null, year: year ? Number(year) : null });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('top.addError'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title={t('top.addTitle')} icon="add_circle">
      <form onSubmit={submit} className="flex flex-col gap-space-sm">
        <div className="grid grid-cols-2 gap-space-sm">
          <Field label={t('top.artist')}><input className="field-input" value={artist} onChange={(e) => setArtist(e.target.value)} required /></Field>
          <Field label={t('top.record')}><input className="field-input" value={album} onChange={(e) => setAlbum(e.target.value)} required /></Field>
          <Field label={t('top.year')}><input className="field-input" type="number" value={year} onChange={(e) => setYear(e.target.value)} /></Field>
        </div>
        {error && <p className="font-body-sm text-body-sm text-priority-urgent">{error}</p>}
        <button className="btn-primary w-full" type="submit" disabled={busy}>{busy ? t('top.saving') : t('top.save')}</button>
      </form>
    </Card>
  );
}

/** Campo de formulario con label + slot. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-space-xs">
      <label className="field-label">{label}</label>
      <div className="field-slot">{children}</div>
    </div>
  );
}
